import type { AcpElicitationData } from '@crowdex/agent-contracts';
import { Button } from '@crowdex/ui/components/button';
import { cn } from '@crowdex/ui/lib/utils';
import { Check, CircleSlash, FormInput } from 'lucide-react';
import { useContext, useState } from 'react';
import { AgentElicitationDialog, elicitationSource } from '@/components/agent-elicitation-dialog';
import { AgentElicitationContext } from '@/lib/conversations/message-part/agent-elicitation-context';
import { TRANSCRIPT_CARD } from './command-card';

function answerSummary(data: AcpElicitationData): string {
  if (data.outcome?.action === 'accept') {
    const values = Object.values(data.outcome.content ?? {}).flatMap((value) =>
      Array.isArray(value) ? value : [String(value)],
    );
    return values.length > 0 ? values.join(', ') : 'Sent';
  }
  if (data.status === 'declined') return 'Declined';
  return 'Cancelled';
}

/**
 * A tool's form in the transcript (design D50): while it waits, a card that opens its dialog
 * (open at first); answered, what was sent.
 */
export function ElicitationCard({ data }: { data: AcpElicitationData }) {
  const interaction = useContext(AgentElicitationContext);
  const [open, setOpen] = useState(true);
  const source = elicitationSource(data);
  if (data.status === 'pending' && interaction !== null) {
    return (
      <>
        <section
          className={cn(
            TRANSCRIPT_CARD,
            'flex items-center gap-3 border-attention px-3.5 py-2.5 text-sm',
          )}
          data-elicitation-card="pending"
        >
          <FormInput aria-hidden className="size-4 shrink-0 text-attention" />
          <span className="min-w-0 flex-1 truncate text-foreground">
            {source === null ? 'The agent needs details' : `${source} needs details`}
          </span>
          <Button className="h-8 rounded-control px-3.5" onClick={() => setOpen(true)} size="sm">
            Answer
          </Button>
        </section>
        <AgentElicitationDialog
          onAnswer={(outcome) => interaction.onAnswer(data.requestId, outcome)}
          onOpenChange={setOpen}
          open={open}
          pendingCount={1}
          request={data}
        />
      </>
    );
  }

  const answered = data.status === 'accepted';
  const Icon = answered ? Check : CircleSlash;
  return (
    <section
      className={cn(TRANSCRIPT_CARD, 'flex items-center gap-3 px-3.5 py-2.5 text-sm')}
      data-elicitation-card="answered"
    >
      <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0">
        <span className="block truncate text-foreground">{data.message}</span>
        <span className="type-meta block truncate text-muted-foreground">
          {answerSummary(data)}
        </span>
      </span>
    </section>
  );
}
